import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';

import { trackDatadogError } from '@/libs/datadog';

import {
  RELOAD_ERROR_CONTEXT,
  RELOAD_FLAG_KEY,
  RETHROW_ERROR_CONTEXT
} from './constants';

/**
 * Wraps `React.lazy` so that a failed dynamic import — typically a 404 on a
 * hashed chunk after a deploy replaced the previous build — triggers a single
 * full-page reload to fetch the latest `index.html` and the new asset URLs it
 * references. The reload is gated by a `sessionStorage` flag so a persistent
 * failure surfaces to the nearest `ErrorBoundary` instead of looping.
 */
// `any` mirrors React's own `lazy<T extends ComponentType<any>>` signature.
// `ComponentType<unknown>` rejects components with specific prop types
// (function components are contravariant in their props), which would block
// every real caller of this helper.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithRetry = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> =>
  lazy(async () => {
    const hasAlreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';

    try {
      const loadedModule = await importer();

      sessionStorage.removeItem(RELOAD_FLAG_KEY);

      return loadedModule;
    } catch (error) {
      if (hasAlreadyReloaded) {
        trackDatadogError(error, RETHROW_ERROR_CONTEXT);

        throw error;
      }

      // The reload below navigates away before any ErrorBoundary sees this
      // failure, so report it here to avoid a silent loss of traceability.
      trackDatadogError(error, RELOAD_ERROR_CONTEXT);

      sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
      location.reload();

      // Keep Suspense in its fallback state while the page navigates — a
      // resolved or rejected promise here would either render stale UI or
      // trip the ErrorBoundary before the reload completes.
      return new Promise<{ default: T }>(() => {});
    }
  });

export default lazyWithRetry;
